import { AxiosError } from 'axios';
import { isArray } from 'lodash';

// NestJS surfaces structured exception bodies as the response `message` field
// when the controller throws `new UnprocessableEntityException({ ... })`. The
// resolver-side INCOMPLETE_TRANSLATIONS shape is the load-bearing example: it
// carries `error`, `locale`, and `missing_fields` so the editor can show a
// useful toast instead of a stringified `[object Object]`.
export interface StructuredErrorMessage {
  error: string;
  locale?: string;
  missing_fields?: readonly string[];
}

export interface BackendError {
  error: string;
  message: string | string[] | StructuredErrorMessage;
  statusCode: number;
}

export class ApiError extends Error {
  statusCode: number;
  headers?: Record<string, any>;

  constructor(message: string, statusCode: number, headers?: Record<string, any>) {
    super(message);
    this.statusCode = statusCode;
    this.headers = headers;
    this.name = 'ApiError';
  }
}

const isStructuredErrorMessage = (value: unknown): value is StructuredErrorMessage =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  'error' in value &&
  typeof (value as { error: unknown }).error === 'string';

// Turn `INCOMPLETE_TRANSLATIONS` / `LOCALE_NOT_ENABLED` into `Incomplete
// translations` / `Locale not enabled` so the bare-code fallback reads as a
// sentence rather than a constant.
const humanizeErrorCode = (code: string): string => {
  const lower = code.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const formatStructuredErrorMessage = (message: StructuredErrorMessage): string => {
  if (
    message.error === 'INCOMPLETE_TRANSLATIONS' &&
    Array.isArray(message.missing_fields) &&
    message.missing_fields.length > 0
  ) {
    const preview = message.missing_fields.slice(0, 3).join(', ');
    const suffix = message.missing_fields.length > 3 ? ', ...' : '';
    const locale = message.locale ?? 'this locale';
    return `Translate ${message.missing_fields.length} missing fields in ${locale} before publishing: ${preview}${suffix}`;
  }
  return humanizeErrorCode(message.error);
};

export const mapToErrors = (error: AxiosError<BackendError> | unknown) => {
  if (error instanceof AxiosError) {
    if (error?.response) {
      const statusCode = error.response.status;
      const headers = error.response.headers;

      const data = error.response.data;

      if (data && data.message) {
        const message = data.message;

        if (isArray(message)) {
          throw new ApiError(message.join('\n'), statusCode, headers);
        }
        if (isStructuredErrorMessage(message)) {
          throw new ApiError(formatStructuredErrorMessage(message), statusCode, headers);
        }
        throw new ApiError(typeof message === 'string' ? message : String(message), statusCode, headers);
      }

      // Some endpoints (e.g. `/customizations/publish-locale`) return the
      // structured payload at the root of `response.data` rather than under a
      // NestJS-wrapped `message` field. Try matching the `StructuredErrorMessage`
      // shape directly so callers still get a useful toast instead of the
      // generic "Request failed with status 422" fallback.
      if (isStructuredErrorMessage(data)) {
        throw new ApiError(formatStructuredErrorMessage(data), statusCode, headers);
      }

      throw new ApiError(`Request failed with status ${statusCode}`, statusCode, headers);
    }

    // Network errors, timeouts, or request setup issues
    if (error.request) {
      console.error('Network error:', error.message, error.code);
      throw new ApiError(error.message || 'Network error occurred. Please check your connection.', 0);
    }

    // Request configuration error
    console.error('Request setup error:', error.message);
    throw new ApiError(error.message || 'Failed to configure request', 500);
  }

  // Non-Axios errors
  const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
  console.error('Unexpected error:', error);
  throw new ApiError(errorMessage, 500);
};
